import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { listAdminNewsletters, fetchAdminCsv } from "../../lib/adminApi";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const Newsletters = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAdminNewsletters({ limit: 1000, skip: 0 });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load newsletter subscribers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((x) => (x.email || "").toLowerCase().includes(needle));
  }, [items, q]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await fetchAdminCsv("/admin/newsletters/export.csv");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "newsletters.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Newsletter CSV exported");
    } catch (err) {
      toast.error(err.message || "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Newsletter Subscribers">
      <div className="space-y-6" data-testid="admin-newsletters-page">
        <Card>
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle>Subscribers ({filtered.length})</CardTitle>
            <div className="flex gap-3">
              <Input
                placeholder="Search email..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-72"
              />
              <Button variant="outline" onClick={load} disabled={loading}>
                Refresh
              </Button>
              <Button onClick={handleExport} disabled={exporting}>
                {exporting ? "Exporting..." : "Export CSV"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-stone-500">Loading newsletter subscribers...</p>
            ) : filtered.length === 0 ? (
              <p className="text-stone-500">No subscribers yet.</p>
            ) : (
              <div className="space-y-2">
                {filtered.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-stone-200 px-4 py-3 flex items-center justify-between"
                  >
                    <span className="text-sm text-stone-800">{item.email}</span>
                    <span className="text-xs text-stone-500">{formatDate(item.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Newsletters;
